
import React, { useState, useEffect } from 'react';
import { Service, SupportProvider } from '../types';
import Modal from './Modal';
import { PlusIcon } from './icons/PlusIcon';
import { EditIcon } from './icons/EditIcon';
import { TrashIcon } from './icons/TrashIcon';
import { 
  getServicesApi, 
  getServiceByIdApi, 
  addServiceApi, 
  updateServiceApi, 
  deleteServiceApi,
  checkServiceAssociationsApi 
} from '../api/serviceApi';

const SUPPORT_PROVIDERS: SupportProvider[] = ['כללית', 'מאוחדת', 'פרטי'];

const Services: React.FC = () => {
  const [services, setServices] = useState<Service[]>([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingService, setEditingService] = useState<Service | null>(null);
  const [serviceName, setServiceName] = useState('');
  const [availableProviders, setAvailableProviders] = useState<SupportProvider[]>([]);
  const [loadingData, setLoadingData] = useState(true);
  const [savingData, setSavingData] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [serviceToDelete, setServiceToDelete] = useState<string | null>(null);
  const [associatedTechnicians, setAssociatedTechnicians] = useState<string[]>([]);
  const [checkingAssociations, setCheckingAssociations] = useState(false);

  // Fetch services on component mount
  useEffect(() => {
    loadServices();
  }, []);

  // Ensure 'פרטי' is always included in availableProviders
  useEffect(() => {
    if (isModalOpen && !availableProviders.includes('פרטי')) {
      setAvailableProviders(prev => [...prev, 'פרטי']);
    }
  }, [isModalOpen, availableProviders]);

  const loadServices = async () => {
    try {
      setLoadingData(true);
      setError(null);
      const data = await getServicesApi();
      // Sort services alphabetically by name
      const sortedData = data.sort((a, b) => a.name.localeCompare(b.name, 'he'));
      setServices(sortedData);
    } catch (err) {
      setError('טעינת השירותים נכשלה');
      console.error(err);
    } finally {
      setLoadingData(false);
    }
  };

  const openModalForAdd = () => {
    setEditingService(null);
    setServiceName('');
    setAvailableProviders(['כללית', 'מאוחדת', 'פרטי']);
    setIsModalOpen(true);
  };

  const openModalForEdit = async (service: Service) => {
    try {
      setSavingData(true);
      // Fetch the latest service data by ID
      const latestService = await getServiceByIdApi(service.id);
      setEditingService(latestService);
      setServiceName(latestService.name);
      // Ensure 'פרטי' is always included
      const providers = latestService.availableProviders.includes('פרטי') 
        ? latestService.availableProviders 
        : [...latestService.availableProviders, 'פרטי'];
      setAvailableProviders(providers);
      setIsModalOpen(true);
    } catch (err) {
      setError('טעינת נתוני השירות נכשלה');
      console.error(err);
    } finally {
      setSavingData(false);
    }
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setEditingService(null);
  };

  const handleProviderToggle = (provider: SupportProvider) => {
    setAvailableProviders((prev) =>
      prev.includes(provider) ? prev.filter((p) => p !== provider) : [...prev, provider]
    );
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!serviceName.trim() || availableProviders.length === 0) return;

    try {
      setSavingData(true);
      setError(null);

      const serviceData = {
        name: serviceName,
        availableDays: editingService?.availableDays ?? [],
        availableProviders,
      };

      if (editingService) {
        // Update existing service
        const updatedService = await updateServiceApi(editingService.id, serviceData);
        setServices(services.map(s => s.id === updatedService.id ? updatedService : s));
      } else {
        // Add new service
        const newService = await addServiceApi(serviceData);
        setServices([...services, newService]);
      }
      
      closeModal();
    } catch (err) {
      setError(editingService ? 'עדכון השירות נכשל' : 'הוספת השירות נכשלה');
      console.error(err);
    } finally {
      setSavingData(false);
    }
  };

  const handleDeleteClick = async (id: string) => {
    setServiceToDelete(id);
    setCheckingAssociations(true);
    setIsDeleteModalOpen(true);
    
    try {
      const technicians = await checkServiceAssociationsApi(id);
      setAssociatedTechnicians(technicians);
    } catch (err) {
      console.error('Error checking associations:', err);
      setAssociatedTechnicians([]);
    } finally {
      setCheckingAssociations(false);
    }
  };

  const handleDeleteConfirm = async () => {
    if (!serviceToDelete) return;

    try {
      setSavingData(true);
      setError(null);
      await deleteServiceApi(serviceToDelete);
      setServices(services.filter(s => s.id !== serviceToDelete));
      setIsDeleteModalOpen(false);
      setServiceToDelete(null);
    } catch (err: any) {
      // Check if it's a 409 conflict error (service associated with technicians)
      if (err.response?.status === 409) {
        const errorData = err.response?.data;
        setError(errorData?.message || 'לא ניתן למחוק את השירות מכיוון שהוא משויך לטכנאים');
      } else {
        setError('מחיקת השירות נכשלה');
      }
      console.error(err);
      setIsDeleteModalOpen(false);
      setServiceToDelete(null);
    } finally {
      setSavingData(false);
    }
  };

  const handleDeleteCancel = () => {
    setIsDeleteModalOpen(false);
    setServiceToDelete(null);
    setAssociatedTechnicians([]);
  };

  return (
    <div className="p-6 max-w-6xl mx-auto">
      <div className="flex justify-between items-center mb-8">
        <div>
          <h3 className="text-3xl font-bold bg-gradient-to-r from-slate-900 to-slate-700 bg-clip-text text-transparent">
            רשימת שירותים
          </h3>
          <p className="text-sm text-slate-500 mt-1">נהל את השירותים הזמינים במערכת</p>
        </div>
        <button
          onClick={openModalForAdd}
          disabled={savingData || loadingData}
          className="flex items-center gap-2 bg-gradient-to-r from-indigo-600 to-indigo-500 text-white px-5 py-2.5 rounded-xl shadow-lg shadow-indigo-200/50 hover:shadow-xl hover:shadow-indigo-300/50 hover:scale-105 active:scale-95 transition-all disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100"
        >
          <PlusIcon className="w-5 h-5" />
          <span className="font-medium">הוסף שירות חדש</span>
        </button>
      </div>

      {error && (
        <div className="mb-4 p-4 bg-red-50 border border-red-200 text-red-700 rounded-lg">
          {error}
        </div>
      )}

      {loadingData ? (
        <div className="space-y-2">
          {[...Array(6)].map((_, index) => (
            <div key={index} className="bg-white/50 backdrop-blur-sm border border-slate-200/60 rounded-2xl p-4 animate-pulse hover:border-slate-300/60 transition-all">
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <div className="h-5 bg-slate-200 rounded w-1/4 mb-2"></div>
                  <div className="flex gap-2 mt-2">
                    <div className="h-6 bg-slate-100 rounded-full w-20"></div>
                    <div className="h-6 bg-slate-100 rounded-full w-20"></div>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <div className="h-9 w-9 bg-slate-100 rounded-full"></div>
                  <div className="h-9 w-9 bg-slate-100 rounded-full"></div>
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="space-y-2">
        {[...services].sort((a, b) => {
          // Sort by isActive: active services first (true before false)
          if (a.isActive === b.isActive) return 0;
          return a.isActive ? -1 : 1;
        }).map((service) => (
          <div 
            key={service.id} 
            className="group bg-white/50 backdrop-blur-sm border border-slate-200/60 rounded-2xl p-4 hover:bg-white hover:border-indigo-200 hover:shadow-lg hover:shadow-indigo-100/50 transition-all duration-300"
          >
            <div className="flex items-center justify-between gap-6">
              <div className="flex-1 min-w-0">
                <h4 className="text-lg font-semibold text-slate-900 mb-2 group-hover:text-indigo-600 transition-colors">
                  {service.name}
                </h4>
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-xs font-medium text-slate-500">קופות חולים:</span>
                  {service.availableProviders.map(provider => (
                    <span 
                      key={provider} 
                      className="px-3 py-1 text-xs font-medium rounded-full bg-gradient-to-r from-indigo-50 to-blue-50 text-indigo-700 border border-indigo-100/50 hover:border-indigo-200 transition-colors"
                    >
                      {provider}
                    </span>
                  ))}
                </div>
              </div>
              <div className="flex items-center gap-2 opacity-60 group-hover:opacity-100 transition-opacity">
                <button 
                  onClick={() => openModalForEdit(service)} 
                  disabled={savingData}
                  className="p-2.5 rounded-xl bg-slate-50 text-slate-600 hover:bg-indigo-50 hover:text-indigo-600 transition-all disabled:opacity-50 disabled:cursor-not-allowed hover:scale-105 active:scale-95"
                  title="ערוך שירות"
                >
                  <EditIcon className="w-5 h-5" />
                </button>
                <button 
                  onClick={() => handleDeleteClick(service.id)} 
                  disabled={savingData}
                  className="p-2.5 rounded-xl bg-slate-50 text-slate-600 hover:bg-red-50 hover:text-red-600 transition-all disabled:opacity-50 disabled:cursor-not-allowed hover:scale-105 active:scale-95"
                  title="מחק שירות"
                >
                  <TrashIcon className="w-5 h-5" />
                </button>
              </div>
            </div>
          </div>
        ))};
        </div>
      )}

      {!loadingData && services.length === 0 && (
        <div className="text-center p-12 bg-gradient-to-br from-slate-50 to-slate-100/50 rounded-2xl border border-slate-200/60 border-dashed">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-slate-200/50 mb-4">
            <PlusIcon className="w-8 h-8 text-slate-400" />
          </div>
          <p className="text-slate-500 font-medium">עדיין לא נוספו שירותים</p>
          <p className="text-sm text-slate-400 mt-1">לחץ על "הוסף שירות חדש" כדי להתחיל</p>
        </div>
      )}

      {!loadingData && services.length === 0 && (
        <div className="text-center p-8 text-slate-500 bg-white rounded-lg shadow-md">
          עדיין לא נוספו שירותים.
        </div>
      )}

      <Modal isOpen={isModalOpen} onClose={closeModal} title={editingService ? 'ערוך שירות' : 'הוסף שירות חדש'}>
        <form onSubmit={handleSubmit}>
          <div className="mb-4">
            <label htmlFor="serviceName" className="block text-sm font-medium text-slate-700 mb-2">שם השירות</label>
            <input
              type="text"
              id="serviceName"
              value={serviceName}
              onChange={(e) => setServiceName(e.target.value)}
              className="w-full p-2 border border-slate-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
              required
            />
          </div>
          <div className="mb-6">
            <label className="block text-sm font-medium text-slate-700 mb-2">קופות חולים זמינות</label>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              {SUPPORT_PROVIDERS.map((provider) => (
                <label key={provider} className={`flex items-center p-3 border border-slate-200 rounded-lg ${provider === 'פרטי' ? 'bg-slate-50 cursor-not-allowed' : 'cursor-pointer hover:bg-slate-50'} transition-colors`}>
                  <input
                    type="checkbox"
                    checked={availableProviders.includes(provider)}
                    onChange={() => handleProviderToggle(provider)}
                    disabled={provider === 'פרטי'}
                    className="h-4 w-4 rounded border-slate-300 text-indigo-600 focus:ring-indigo-500 disabled:opacity-50"
                  />
                  <span className={`text-sm ms-3 ${provider === 'פרטי' ? 'text-slate-500' : 'text-slate-700'}`}>{provider}</span>
                </label>
              ))}
            </div>
          </div>
          <div className="flex justify-end space-s-4">
            <button 
              type="button" 
              onClick={closeModal} 
              disabled={savingData}
              className="px-4 py-2 bg-slate-100 text-slate-700 rounded-md hover:bg-slate-200 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              ביטול
            </button>
            <button 
              type="submit" 
              disabled={savingData}
              className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              {savingData && (
                <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
              )}
              {savingData ? 'שומר...' : 'שמור'}
            </button>
          </div>
        </form>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal isOpen={isDeleteModalOpen} onClose={handleDeleteCancel} title="מחיקת שירות">
        <div className="py-4">
          <div className="flex items-center justify-center w-16 h-16 mx-auto mb-4 rounded-full bg-red-100">
            <TrashIcon className="w-8 h-8 text-red-600" />
          </div>
          
          {checkingAssociations ? (
            <div className="text-center py-4">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
              <p className="mt-2 text-sm text-slate-600">בודק קשרים לטכנאים...</p>
            </div>
          ) : associatedTechnicians.length > 0 ? (
            <>
              <p className="text-center text-lg font-medium text-slate-900 mb-2">
                לא ניתן למחוק שירות זה
              </p>
              <p className="text-center text-sm text-slate-600 mb-4">
                השירות משויך ל-{associatedTechnicians.length} טכנאי/ם:
              </p>
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6">
                <ul className="space-y-2">
                  {associatedTechnicians.map((techName, index) => (
                    <li key={index} className="flex items-center text-sm text-amber-900">
                      <span className="w-2 h-2 bg-amber-500 rounded-full me-2"></span>
                      {techName}
                    </li>
                  ))}
                </ul>
              </div>
              <p className="text-center text-sm text-slate-600 mb-6">
                אנא הסר את השירות מהטכנאים הללו לפני מחיקתו.
              </p>
              <div className="flex justify-center">
                <button 
                  type="button" 
                  onClick={handleDeleteCancel} 
                  className="px-4 py-2 bg-slate-100 text-slate-700 rounded-xl hover:bg-slate-200 transition-colors"
                >
                  סגור
                </button>
              </div>
            </>
          ) : (
            <>
              <p className="text-center text-lg font-medium text-slate-900 mb-2">
                האם אתה בטוח?
              </p>
              <p className="text-center text-sm text-slate-600 mb-6">
                פעולה זו תמחק את השירות לצמיתות ולא ניתן לשחזר אותו.
              </p>
              <div className="flex gap-3 justify-end">
                <button 
                  type="button" 
                  onClick={handleDeleteCancel} 
                  disabled={savingData}
                  className="px-4 py-2 bg-slate-100 text-slate-700 rounded-xl hover:bg-slate-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  ביטול
                </button>
                <button 
                  type="button"
                  onClick={handleDeleteConfirm} 
                  disabled={savingData}
                  className="px-4 py-2 bg-gradient-to-r from-red-600 to-red-500 text-white rounded-xl hover:from-red-700 hover:to-red-600 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                >
                  {savingData && (
                    <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                  )}
                  {savingData ? 'מוחק...' : 'מחק שירות'}
                </button>
              </div>
            </>
          )}
        </div>
      </Modal>
    </div>
  );
};

export default Services;
